
import { Component, Prop } from "vue-property-decorator";
import { EpgItem, MyChannel } from "@/services/apiService/api.types";
import { Action, Getter, Mutation } from "vuex-class";
import { Namespace } from "@/store/types";
import SpatialNavigation from "spatial-navigation-js";
import { keyCodeFinder } from "@/services/keyCodeService/keyCodeService";
import { KeySet } from "@/services/keyCodeService/keyCode.types";
import {
  ChannelsPageGetter,
  ChannelsPageMutation,
  InfoChannelData,
  ModalType
} from "@/components/channelsPage/types";
import { RouteName } from "@/router/types";
import {
  ChannelChangeAction,
  ChannelChangeMutation
} from "@/components/channelChange/types";
import { ChannelEpgParams, OverlayAction } from "@/components/overlay/types";
import dayjs from "dayjs";
import { digitCount } from "@/helpers/utils";
import { mixins } from "vue-class-component";
import { EpgHelpers } from "@/mixins/EpgHelpers";
import { EventBus } from "@/main";
import apiStorageInterface from "@/services/apiStorageInterface";
import {
  BuffetGetter,
  BuffetMutation,
  defaultChannel,
  PlayedEpgItemCoordinates
} from "@/components/buffet/types";
import { BuffetCategories } from "@/helpers/constants";
import { scrollToPage } from "@/helpers/utils";
import { LocaleBuffetEPGCardFilter } from "@/plugins/i18n/types";

@Component({})
export default class BuffetEpgCard extends mixins(EpgHelpers) {
  @Prop() readonly epgItem?: EpgItem;
  @Prop() readonly swiperIndex?: number;
  @Prop() readonly epgItemIndex?: number;
  @Prop() readonly buffetCategory?: BuffetCategories;

  @Action(ChannelChangeAction.UpdateSource, {
    namespace: Namespace.ChannelChange
  })
  updateSource!: (src: string) => void;

  @Action(ChannelChangeAction.UpdateOffset, {
    namespace: Namespace.ChannelChange
  })
  updateOffset!: (offset: number) => void;

  @Action(OverlayAction.UpdateChannelEpg, {
    namespace: Namespace.Overlay
  })
  updateChannelEpg!: (channelEpgParams?: ChannelEpgParams) => Promise<void>;

  @Mutation(ChannelsPageMutation.setCardControls, {
    namespace: Namespace.ChannelsPage
  })
  setControlsActive!: (cardControls: boolean) => void;

  @Mutation(ChannelChangeMutation.SetChannelId, {
    namespace: Namespace.ChannelChange
  })
  setChannelId!: (channelId: number) => void;

  @Mutation(ChannelsPageMutation.setActiveChannelOverlayInfo, {
    namespace: Namespace.ChannelsPage
  })
  setActiveChannelOverlayInfo!: (channelInfo?: InfoChannelData) => void;

  @Mutation(ChannelsPageMutation.setActiveInfoScreenEpgItem, {
    namespace: Namespace.ChannelsPage
  })
  setActiveInfoScreenEpgItem!: (epgItem?: EpgItem) => void;

  @Mutation(ChannelsPageMutation.setShowInfoScreen, {
    namespace: Namespace.ChannelsPage
  })
  setShowInfoScreen!: (showInfoScreen: boolean) => void;

  @Mutation(ChannelsPageMutation.setShowEpgScreen, {
    namespace: Namespace.ChannelsPage
  })
  setShowEpgScreen!: () => void;

  @Mutation(BuffetMutation.setPlayedEpgItemId, { namespace: Namespace.Buffet })
  setPlayedEpgItemId!: (epgId?: number) => void;

  @Mutation(BuffetMutation.setPlayedEpgItemPos, { namespace: Namespace.Buffet })
  setPlayedEpgItemPos!: (slidePos?: PlayedEpgItemCoordinates) => void;

  @Getter(ChannelsPageGetter.getShowEpgScreen, {
    namespace: Namespace.ChannelsPage
  })
  getShowEpgScreen!: boolean;

  @Getter(ChannelsPageGetter.getShowInfoScreen, {
    namespace: Namespace.ChannelsPage
  })
  getShowInfoScreen!: boolean;

  @Getter(ChannelsPageGetter.getActiveInfoScreenEpgItem, {
    namespace: Namespace.ChannelsPage
  })
  getActiveInfoScreenEpgItem!: EpgItem | undefined;

  @Getter(BuffetGetter.getPlayedEpgItemId, {
    namespace: Namespace.Buffet
  })
  getPlayedEpgItemId!: number | undefined;

  @Getter(BuffetGetter.getPlayedEpgItemCat, {
    namespace: Namespace.Buffet
  })
  getPlayedEpgItemCat!: BuffetCategories;

  @Getter(BuffetGetter.getPlayedEpgItemDate, {
    namespace: Namespace.Buffet
  })
  getPlayedEpgItemDate!: string;

  @Getter(ChannelsPageGetter.GetCardControls, {
    namespace: Namespace.ChannelsPage
  })
  getCardControlsStatus!: boolean;

  showControls = false;
  focusedControl = 0;

  card: HTMLElement | null = null; //var that stores reference to this card in the DOM
  isCardFocused = false;

  modalTypes = ModalType;

  channel: MyChannel = defaultChannel;

  scrollFuncRef = scrollToPage;

  LocaleBuffetEPGCardFilter = LocaleBuffetEPGCardFilter;

  async mounted(): Promise<void> {
    this.card = document.getElementById("epgItem" + this.epgItem?.id);
    if (this.card) {
      this.card.addEventListener("focusout", this.focusChanged);
    }

    //channel data for this EPG Item
    const channels = await apiStorageInterface.fetchCustomChannelList();
    if (channels.find((channel) => channel.id === this.epgItem?.channel_id)) {
      this.channel = channels.find(
        (channel) => channel.id === this.epgItem?.channel_id
      ) as MyChannel;
    }

    if (!this.channel.catchup) {
      //play btn is disabled, epg btn is default
      this.focusedControl = 1;
    }
    //focusing previously played EPG item and setting the data required
    if (
      this.getPlayedEpgItemId === this.epgItem?.id &&
      this.getPlayedEpgItemCat === this.buffetCategory &&
      this.getPlayedEpgItemCat !== BuffetCategories.Home &&
      this.getPlayedEpgItemDate ===
        dayjs(this.epgItem?.endtime).format("YYYY-MM-DD") &&
      !this.getShowEpgScreen &&
      !this.getShowInfoScreen
    ) {
      SpatialNavigation.resume();
      SpatialNavigation.focus("#epgItem" + this.epgItem?.id);
      this.setControlsActive(false);
      this.setPlayedEpgItemId();
    }

    //focusing channel after closing overlay
    EventBus.$on("infoOverlayClosed", (epgId: number) => {
      if (this.epgItem && this.epgItem.id === epgId && !this.getShowEpgScreen) {
        SpatialNavigation.resume();
        setTimeout(() => {
          SpatialNavigation.focus("#epgItem" + this.epgItem?.id);
          this.setControlsActive(false);
        }, 50);
      }
    });

    //focusing channel after closing overlay
    EventBus.$on("epgOverlayClosed", (epgId: number) => {
      if (this.epgItem && this.epgItem.id === epgId) {
        SpatialNavigation.resume();
        setTimeout(() => {
          SpatialNavigation.focus("#epgItem" + this.epgItem?.id);
          this.setControlsActive(false);
        }, 50);
      }
    });

    //plays focused channel if overlay play btn was triggered(more convenient then copying play channel logic to every overlay)
    EventBus.$on("playChannel", (epgId: number) => {
      if (this.epgItem && this.epgItem.id === epgId) {
        this.playChannel();
      }
    });
  }

  get playIcon(): string {
    return require("/src/assets/svg/svg-home/02-buffet.svg");
  }

  get epgIcon(): string {
    return require("/src/assets/svg/svg-home/09-epg.svg");
  }

  get InfoIcon(): string {
    return require("/src/assets/svg/svg-home/10-info.svg");
  }

  get imgPlaceholder(): string {
    return require("/src/assets/images/square-image-placeholder.jpg");
  }

  get lockIconPurple(): string {
    return require("/src/assets/svg/svg-settings/02-lock-icon-purple.svg");
  }

  get channelNum(): string {
    let channelNum = "";
    if (this.channel) {
      for (let i = 0; i < 3 - digitCount(this.channel?.id); i++) {
        channelNum += "0";
      }
      return channelNum + this.channel.id + ".";
    }

    return "000";
  }

  handleCardControls(event: KeyboardEvent): void {
    SpatialNavigation.pause();
    switch (keyCodeFinder(event.keyCode)) {
      case KeySet.KEY_ENTER:
        if (!this.showControls) {
          this.openControls();
          return;
        }
        switch (this.focusedControl) {
          case 0:
            this.playChannel();
            break;
          case 1:
            this.openModalScreen(this.modalTypes.EpgModal);
            break;
          case 2:
            this.openModalScreen(this.modalTypes.InfoModal);
            break;
        }
        break;
      case KeySet.KEY_RIGHT:
        if (this.focusedControl < 2 && this.showControls) {
          this.focusedControl++;
        }
        break;
      case KeySet.KEY_LEFT:
        if (!this.channel.catchup) {
          if (this.focusedControl > 1) {
            this.focusedControl--;
          }
          return;
        }
        if (this.focusedControl > 0) {
          this.focusedControl--;
        }
        break;
      case KeySet.KEY_BACK:
        this.showControls = false;
        this.setControlsActive(false);
        SpatialNavigation.resume();
        break;
    }
  }

  openModalScreen(modalType: ModalType): void {
    if (this.epgItem) {
      const channelInfo: InfoChannelData = {
        channelName: this.channel.name,
        channelId: this.channel.id,
        channelCatchup: !!this.channel.catchup
      };

      this.setActiveChannelOverlayInfo(channelInfo);
      if (modalType === ModalType.InfoModal) {
        this.setActiveInfoScreenEpgItem(this.epgItem);
        this.setShowInfoScreen(true);
      }
      if (modalType === ModalType.EpgModal) {
        this.setActiveInfoScreenEpgItem(this.epgItem);
        this.setShowEpgScreen();
      }
    }
  }

  openControls(): void {
    this.showControls = true;
    this.setControlsActive(true);
  }

  focusChanged(): void {
    if (document.activeElement?.id !== "epgItem" + this.epgItem?.id) {
      this.showControls = false;
      this.isCardFocused = false;
    }
  }

  async playChannel(): Promise<void> {
    if (this.epgItem && this.channel.catchup) {
      //setting player and overlay required info
      this.updateSource(this.channel.videoLive);
      this.setChannelId(this.channel.id);
      this.setPlayedEpgItemId(this.epgItem.id);

      let offset = 0;
      if (this.epgItem) {
        offset = dayjs().diff(this.epgItem.starttime, "second");
      }
      this.updateOffset(offset);
      await this.updateChannelEpg({
        date: dayjs(this.getActiveInfoScreenEpgItem?.starttime).format(
          "YYYY-MM-DD"
        )
      });

      //setting position for buffet home page
      if (this.buffetCategory === BuffetCategories.Home) {
        this.setPlayedEpgItemPos({
          swiperIndex: this.swiperIndex ? this.swiperIndex : 0,
          slideIndex: this.epgItemIndex ? this.epgItemIndex : 0
        });
      }

      await this.$router.push({ name: RouteName.Player });
    }
  }

  clickHandler(): void {
    SpatialNavigation.focus("#epgItem" + this.epgItem?.id);
    this.isCardFocused = true;
    this.openControls();
  }

  beforeDestroy(): void {
    if (this.card) {
      this.card.removeEventListener("focusout", this.focusChanged);
    }
    EventBus.$off("playChannel");
    //leaving it true in the store could produce navigation issues
    if (this.getCardControlsStatus) {
      this.setControlsActive(false);
    }
  }
}
